import { useEffect, useLayoutEffect, useRef } from 'react'

export function isEditableElement(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  const tag = el.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
  if (el.isContentEditable || el.getAttribute('role') === 'textbox') return true
  return false
}

/**
 * Single global keydown listener on document.
 *
 * The previous shape `useEffect(() => ..., [handler])` re-registered the
 * listener on every render that changed handler identity. Shell.tsx's
 * shortcutHandler depends on ~20 pieces of state + several hook objects,
 * so the add/remove pair fired on practically every render — not wrong,
 * but wasteful, and it created a window between `removeEventListener`
 * and the next `addEventListener` where keystrokes were dropped.
 *
 * We now stash the caller's handler in a ref and register exactly ONE
 * listener for the component's lifetime. The listener reads handlerRef
 * at event time, so updates are observed without re-binding the DOM.
 */
export function useShortcuts(handler: (e: KeyboardEvent) => void) {
  const handlerRef = useRef(handler)
  // useLayoutEffect (not plain useEffect or in-render assignment): this has
  // to run synchronously after commit so a keydown fired during the same
  // paint-tick sees the latest handler. A render-phase assignment trips
  // React's purity lint; a regular useEffect introduces a frame of lag
  // where keydowns still run the stale handler.
  useLayoutEffect(() => {
    handlerRef.current = handler
  }, [handler])
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => handlerRef.current(e)
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])
}
